import React, { useState } from 'react';
import { X, Plus, Lock, Trash2, Loader2 } from 'lucide-react';
import FormFieldList from '../shared/FormFieldList';
import { MobilePropertyResponsesSection } from '../../formResponses/PropertyResponsesSection';
import TagsInput from '../../ui/TagsInput';
import Toast from '../../Toast/Toast';

const MobilePropertyDrawer = ({ propertyTab, availableTags }) => {
  const [deleteFieldConfirm, setDeleteFieldConfirm] = useState(null);
  const [isFieldDeleting, setIsFieldDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  if (!propertyTab.showPropertyDrawer) return null;

  const { currentProperty, handleCloseDrawer, activeDrawerTab, setActiveDrawerTab, handleUpdateField, handleDeleteProperty, isDirty, saving, handleSaveProperty } = propertyTab;

  const onSave = async () => {
    const ok = await handleSaveProperty();
    setToast(ok
      ? { message: 'Property saved successfully', type: 'success' }
      : { message: 'Failed to save property', type: 'error' }
    );
  };

  const handleFieldDeleteClick = (fieldName) => {
    setDeleteFieldConfirm(fieldName);
  };

  const handleConfirmFieldDelete = async () => {
    if (!deleteFieldConfirm) return;
    setIsFieldDeleting(true);
    try {
      const success = propertyTab.handleRemoveFormField(deleteFieldConfirm);
      if (success === false) {
        setToast({ message: 'Cannot delete this field.', type: 'error' });
      } else {
        setToast({ message: 'Custom field deleted successfully', type: 'success' });
      }
    } catch {
      setToast({ message: 'Failed to delete field. Please try again.', type: 'error' });
    } finally {
      setIsFieldDeleting(false);
      setDeleteFieldConfirm(null);
    }
  };

  const mergeCaptureFields = () => {
    const existing = currentProperty.captureFormFields || [];
    const required = [
      { name: 'First Name', locked: true },
      { name: 'Last Name', locked: true },
      { name: 'Email', locked: true },
      { name: 'Phone', locked: true },
      { name: 'Address', locked: true },
    ];
    const merged = [...required];
    existing.forEach((f) => {
      const fieldName = (f.name || '').toLowerCase();
      // Skip legacy 'Name' field — replaced by First Name + Last Name
      if (fieldName === 'name') return;
      if (!merged.some(m => m.name.toLowerCase() === fieldName)) {
        merged.push(f);
      }
    });
    return merged;
  };

  return (
    <>
      <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-40" onClick={handleCloseDrawer}></div>
      <div className="fixed inset-x-0 bottom-0 bg-stone-50 rounded-t-2xl shadow-sheet z-50 flex flex-col h-[90vh] animate-slideUp">
        <div className="bg-white px-5 pt-3 pb-4 rounded-t-2xl border-b border-notion-border flex-shrink-0">
          <div className="w-full flex justify-center mb-3" onClick={handleCloseDrawer}>
            <div className="w-12 h-1.5 bg-stone-300 rounded-full"></div>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-bold text-notion-text">{currentProperty.address || 'Untitled Property'}</h2>
              <p className="text-[10px] text-stone-400">
                {isDirty && !saving ? <span className="text-amber-600">Unsaved changes</span> : 'Editing Property Site'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onSave}
                disabled={!isDirty || saving}
                className="bg-stone-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:bg-stone-400 disabled:cursor-not-allowed"
              >
                {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleDeleteProperty} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleCloseDrawer} className="p-1.5 bg-stone-100 rounded-full text-stone-500">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex border-b border-notion-border mt-4">
            {['editor', 'form', 'responses'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveDrawerTab(tab)}
                className={`flex-1 pb-2 text-xs font-medium border-b-2 transition-colors ${activeDrawerTab === tab
                  ? 'border-stone-800 text-stone-800'
                  : 'border-transparent text-stone-400'
                  }`}
              >
                {tab === 'editor' ? 'Editor' : tab === 'form' ? 'Form' : `Responses${propertyTab.responseCount > 0 ? ` (${propertyTab.responseCount})` : ''}`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 pb-24">
          {activeDrawerTab === 'editor' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="block text-[11px] text-stone-500 font-medium mb-1">Address Line 1</label>
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={currentProperty.address || ''}
                    onChange={(e) => handleUpdateField('address', e.target.value)}
                    className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[11px] text-stone-500 font-medium mb-1">Address Line 2</label>
                  <input
                    type="text"
                    placeholder="Apartment, Suite, etc. (optional)"
                    value={currentProperty.address2 || ''}
                    onChange={(e) => handleUpdateField('address2', e.target.value)}
                    className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-[11px] text-stone-500 font-medium mb-1">Price</label>
                    <input
                      type="text"
                      value={currentProperty.price || ''}
                      onChange={(e) => handleUpdateField('price', e.target.value)}
                      className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[11px] text-stone-500 font-medium mb-1">Sq Ft</label>
                    <input
                      type="text"
                      value={currentProperty.sqft || ''}
                      onChange={(e) => handleUpdateField('sqft', e.target.value)}
                      className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-[11px] text-stone-500 font-medium mb-1">Bedrooms</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Bedrooms"
                      value={currentProperty.bedrooms || 0}
                      onChange={(e) => handleUpdateField('bedrooms', parseInt(e.target.value) || 0)}
                      className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[11px] text-stone-500 font-medium mb-1">Bathrooms</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Bathrooms"
                      value={currentProperty.bathrooms || 0}
                      onChange={(e) => handleUpdateField('bathrooms', parseInt(e.target.value) || 0)}
                      className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none focus:border-stone-400"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="block text-[11px] text-stone-500 font-medium mb-1">Description</label>
                  <textarea
                    placeholder="Description"
                    rows="4"
                    value={currentProperty.description || ''}
                    onChange={(e) => handleUpdateField('description', e.target.value)}
                    className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-xs outline-none resize-none focus:border-stone-400"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Photos ({currentProperty.imageUrls?.length || 0})</h3>
                <div className="grid grid-cols-3 gap-2">
                  {currentProperty.imageUrls?.slice(0, 2).map((url, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-stone-200 rounded-lg bg-cover bg-center"
                      style={{ backgroundImage: `url('${url}')` }}
                    ></div>
                  ))}
                  {(currentProperty.imageUrls?.length || 0) < 15 && (
                    <label className="aspect-square border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center text-stone-400 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            propertyTab.handleUploadImage(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                      />
                      <Plus className="w-5 h-5" />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeDrawerTab === 'form' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider">Capture Form Fields</h3>
                <div className="flex flex-col gap-2">
                  {mergeCaptureFields().map((field, idx) => (
                    <div key={idx} className="relative group">
                      <input
                        type="text"
                        value={field.name}
                        readOnly
                        disabled={field.locked}
                        className={`w-full border border-notion-border rounded px-3 py-2 text-xs outline-none ${field.locked ? 'bg-stone-100 text-stone-400' : 'bg-white text-stone-600 pr-8'}`}
                      />
                      {field.locked ? (
                        <Lock className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-300" />
                      ) : (
                        <button
                          onClick={() => handleFieldDeleteClick(field.name)}
                          title="Delete field"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-300 hover:text-red-500 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add custom field (e.g. Move-in Date)"
                      className="flex-1 bg-white border border-notion-border rounded px-3 py-2 text-xs outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          propertyTab.handleAddFormField(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      className="px-3 py-2 bg-stone-800 text-white rounded text-xs font-medium hover:bg-stone-700 transition"
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling;
                        if (input.value.trim()) {
                          propertyTab.handleAddFormField(input.value);
                          input.value = '';
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Post-Submission Automations</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-white border border-notion-border rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!currentProperty.automations?.createLead}
                      onChange={(e) => propertyTab.handleToggleAutomation('createLead', e.target.checked)}
                      className="accent-stone-800 w-4 h-4 rounded"
                    />
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-stone-700">Create Lead in CRM</span>
                      <span className="text-[10px] text-stone-400">Automatically create a lead when this form is submitted</span>
                    </div>
                  </label>

                  {/* Auto-assign tags when createLead is enabled */}
                  {!!currentProperty.automations?.createLead && (
                    <div className="ml-7 mt-1">
                      <label className="block text-[10px] text-stone-500 font-medium mb-1.5">Auto-assign Tags</label>
                      <TagsInput
                        tags={currentProperty.automations?.tags || []}
                        onChange={(newTags) => propertyTab.handleAutomationTagsChange(newTags)}
                        suggestions={availableTags}
                        placeholder="Add tag and press Enter..."
                        size="md"
                      />
                      <p className="text-[10px] text-stone-400 mt-1">Tags applied to leads from this form</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeDrawerTab === 'responses' && (
            <MobilePropertyResponsesSection
              propertyId={currentProperty.id}
              propertyName={currentProperty.address || 'Untitled Property'}
            />
          )}
        </div>
      </div>

      {/* Delete Field Confirmation Modal */}
      {deleteFieldConfirm && (
        <div className="fixed inset-0 bg-stone-900/20 flex items-center justify-center z-[60]" onClick={() => !isFieldDeleting && setDeleteFieldConfirm(null)}>
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Custom Field</h3>
            <p className="text-sm text-stone-600 mb-1">
              Are you sure you want to delete <strong>{deleteFieldConfirm}</strong>?
            </p>
            <p className="text-xs text-stone-400 mb-4">
              All future form submissions will no longer include this field.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteFieldConfirm(null)}
                disabled={isFieldDeleting}
                className="flex-1 py-2 border border-notion-border rounded text-stone-600 text-sm font-medium hover:bg-stone-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFieldDelete}
                disabled={isFieldDeleting}
                className="flex-1 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
              >
                {isFieldDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default MobilePropertyDrawer;
